import { createContext, useContext } from "react";
import { AdjustFontSizeToFitContent } from "@newsela/angelou";
import { EXPORTABLES_PREVIEW_DO_NOT_PRINT_CLASSNAME } from "@/components/AIComponents/ExportablesModal/ExportablesModal.constants";
import {
  textareaStyles,
  paragraphStyles,
  characterCountStyles,
  containerStyles,
  inputStyles,
} from "./TemplateTextArea.styles";

interface TemplateTextAreaContextType {
  value: string;
  readOnly: boolean;
  maxLength?: number | undefined;
}

const TemplateTextAreaContext =
  createContext<TemplateTextAreaContextType | null>(null);

function useTemplateTextAreaContext() {
  const context = useContext(TemplateTextAreaContext);
  if (!context) {
    throw new Error(
      "TemplateTextArea subcomponents must be used within <TemplateTextArea>",
    );
  }
  return context;
}

export interface TemplateTextAreaProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value?: string | undefined;
  readOnly?: boolean | undefined;
  maxLength?: number | undefined;
}

export function TemplateTextArea({
  value = "",
  readOnly = false,
  maxLength,
  children,
  ...rest
}: TemplateTextAreaProps) {
  return (
    <TemplateTextAreaContext.Provider value={{ value, readOnly, maxLength }}>
      <div css={containerStyles} {...rest}>
        {children}
      </div>
    </TemplateTextAreaContext.Provider>
  );
}

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export function TextArea(props: TextAreaProps) {
  const { value, readOnly, maxLength } = useTemplateTextAreaContext();

  if (readOnly) return null;

  return (
    <AdjustFontSizeToFitContent>
      <textarea
        css={textareaStyles}
        value={value}
        maxLength={maxLength}
        {...props}
      />
    </AdjustFontSizeToFitContent>
  );
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

export function Input(props: InputProps) {
  const { value, readOnly, maxLength } = useTemplateTextAreaContext();

  if (readOnly) return null;

  return (
    <AdjustFontSizeToFitContent>
      <input css={inputStyles} value={value} maxLength={maxLength} {...props} />
    </AdjustFontSizeToFitContent>
  );
}

export type ParagraphProps = React.HTMLAttributes<HTMLParagraphElement>;

export function Paragraph(props: ParagraphProps) {
  const { value, readOnly } = useTemplateTextAreaContext();

  if (!readOnly) return null;

  return (
    <AdjustFontSizeToFitContent>
      <p css={paragraphStyles} {...props}>
        {value}
      </p>
    </AdjustFontSizeToFitContent>
  );
}

export type CharacterCountProps = React.HTMLAttributes<HTMLSpanElement>;

export function CharacterCount(props: CharacterCountProps) {
  const { value, readOnly, maxLength } = useTemplateTextAreaContext();

  if (readOnly || maxLength === undefined) return null;

  return (
    <span
      css={characterCountStyles}
      {...props}
      className={[props.className, EXPORTABLES_PREVIEW_DO_NOT_PRINT_CLASSNAME]
        .filter(Boolean)
        .join(" ")}
    >
      {value.length}/{maxLength}
    </span>
  );
}

TemplateTextArea.TextArea = TextArea;
TemplateTextArea.Input = Input;
TemplateTextArea.Paragraph = Paragraph;
TemplateTextArea.CharacterCount = CharacterCount;

export default TemplateTextArea;
